import React, { useEffect, useRef, useState } from "react";
import ImageSlideShow from "../../../../Components/ImageSlideShow/ImageSlideShow";
import {
	useDeleteOwnerInvitationMemoryMutation,
	useLazyGetWeddingInvitationMemoriesQuery,
	useLazyGetWeddingInvitationQuery,
	useLazyValidateWeddingInvitationQuery,
	useUpdateOwnerInvitationMemoryMutation
} from "../../../../store/services/invitations/invitations.service";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import langEN from "../../../../Components/Templates/WeddingTemplates/lang.EN.json";
import langAR from "../../../../Components/Templates/WeddingTemplates/lang.AR.json";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import {
	Typography,
	Button,
	Box,
	Pagination,
	Skeleton,
	Stack,
	useMediaQuery,
	useTheme,
	Menu,
	MenuItem,
	IconButton,
	Alert,
	CircularProgress
} from "@mui/material";
import {
	IGallery,
	INVITATION_PRICING_PLANS_ENUM,
	Invitations
} from "../../../../store/services/invitations/dto/invitations.dto";
import {
	Delete,
	DownloadOutlined,
	Share,
	MoreVertTwoTone,
	LockOpen,
	Lock,
	PhotoLibrary,
	CameraAltSharp
} from "@mui/icons-material";
import { MemoriesWrapper, LockedMemoriesWrapper } from "./styles";
import ConfirmModal from "../../../../Components/Modals/ConfirmModal";
import { toast } from "react-toastify";
import { notifyError } from "../../../../utils/NotifyError";
import { IError } from "../../../../utils/getErrorMessage";
import ShareWeddingInvitationMemoriesModal from "../../../../Components/Modals/ShareModal/ShareWeddingMemoriesModal";
import LockedInvitation from "../../../../Components/LockedInvitation";
import NoDataFallback from "../../../../Components/NoDataFallback";
import RsvpModal from "../../../../Components/Modals/RSVP/RSVP";
import { LANG_EN } from "../../../../Components/Templates/WeddingTemplates/constants";
import { getFullInvitationsState, setOpenPricingModal } from "../../../../store/slices/invitations/invitations.slice";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { UpgradeBtn, WhiteCrown } from "../../styles";
import { trackEvent } from "../../../../utils/analytics";
import { trackMetaEvent } from "../../../../utils/metaPixel";
import { trackTikTokEvent } from "../../../../utils/tiktokPixel";

const MEMORIES_PER_PAGE = 15;
const loadingArray = new Array(MEMORIES_PER_PAGE).fill(0);

const MediaItem = ({
	item,
	loadingCount,
	setLoadingCount,
	totalItems,
	onDelete,
	onUpdate,
	isManagedRoute,
	onImageClick
}: {
	item: IGallery;
	loadingCount: number;
	setLoadingCount: React.Dispatch<React.SetStateAction<number>>;
	totalItems: number;
	onDelete: (id: string) => void;
	onUpdate: (id: string, state: boolean) => void;
	isManagedRoute: boolean;
	onImageClick?: () => void;
}) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);

	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	useEffect(() => {
		if (!item.video_url || !videoRef.current) return;

		const video = videoRef.current;

		// Force reload
		video.src = item.video_url;
		video.load();

		// Try play/pause trick (iOS hint)
		const tryPlay = async () => {
			try {
				await video.play();
				video.pause();
			} catch {}
		};

		tryPlay();
	}, [item.video_url]);

	return (
		<ImageListItem key={item._id}>
			{item?.is_private && (
				<Box className={"private_memory"} style={{ opacity: loadingCount < totalItems ? 0 : 1 }}>
					<Lock style={{ marginRight: "5px" }} />
				</Box>
			)}

			{item.video_url ? (
				<video
					ref={videoRef}
					controls
					playsInline
					muted
					preload="metadata"
					style={{ width: "100%", opacity: loadingCount < totalItems ? 0 : 1 }}
					onLoadedData={() => setLoadingCount(prevState => prevState + 1)}
				>
					{item.video_url && <source src={item.video_url} type="video/mp4" />}
				</video>
			) : (
				<img
					srcSet={`${item.image_url}`}
					src={`${item.image_url}`}
					alt={item.user_email}
					onLoad={e => {
						setLoadingCount(prevState => prevState + 1);
					}}
					style={{ opacity: loadingCount < totalItems ? 0 : 1, cursor: !isManagedRoute ? "pointer" : undefined }}
					onClick={!isManagedRoute ? onImageClick : undefined}
				/>
			)}
			{loadingCount < totalItems || !isManagedRoute ? null : (
				<>
					<IconButton
						className={"memory_menu"}
						onClick={handleClick}
						style={{ opacity: loadingCount < totalItems ? 0 : 1 }}
					>
						<MoreVertTwoTone />
					</IconButton>
					<Menu
						id={`memory-menu-${item._id}`}
						anchorEl={anchorEl}
						open={!!anchorEl}
						onClose={handleClose}
						MenuListProps={{
							"aria-labelledby": `memory-menu-${item._id}`
						}}
					>
						<MenuItem onClick={() => onUpdate(item._id, !!item.is_private)}>
							{!item.is_private ? (
								<>
									<Lock style={{ marginRight: "5px" }} />
									Mark as private
								</>
							) : (
								<>
									<LockOpen style={{ marginRight: "5px" }} />
									Mark as public
								</>
							)}
						</MenuItem>
						<MenuItem onClick={() => onDelete(item._id)}>
							<Delete style={{ marginRight: "5px" }} /> Delete
						</MenuItem>
					</Menu>
				</>
			)}
		</ImageListItem>
	);
};

const Memories = () => {
	const [deleteImageId, setDeleteImageId] = useState<null | string>(null);
	const [updateImage, setUpdateImage] = useState<null | { id: String; state: boolean }>(null);
	const [slideShowOpen, setSlideShowOpen] = useState(false);
	const [slideShowIndex, setSlideShowIndex] = useState(0);
	const params = useParams();
	const id = params.id;
	const [searchParams] = useSearchParams();
	const dispatch = useAppDispatch();
	const password = searchParams.get("password");
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const [page, setPage] = useState(1);
	const [loadingCount, setLoadingCount] = useState(0);
	const [fetchMemories, { data, isLoading: isFetchingMemories }] = useLazyGetWeddingInvitationMemoriesQuery();
	const [callDeleteMemory, { isLoading: isDeletingMemory }] = useDeleteOwnerInvitationMemoryMutation();
	const [callUpdateMemory, { isLoading: isUpdatingMutation }] = useUpdateOwnerInvitationMemoryMutation();
	const [shareInvitationMemories, setShareInvitationMemories] = useState(false);
	const [fetchInvitation, { isLoading: isFetchingInvitation }] = useLazyGetWeddingInvitationQuery();
	const { selectedInvitation } = useAppSelector(getFullInvitationsState);
	const [callValidateInvitation, { isLoading: isValidatingInvitation, error: validationError }] =
		useLazyValidateWeddingInvitationQuery();
	const location = useLocation();
	const isManagedRoute = location.pathname.includes("/manageWeddingInvitation");
	const imageOnlyItems = (data?.items || []).filter(item => !item.video_url);
	const [showCommentModal, setShowCommentModal] = useState(false);
	const lang = selectedInvitation?.language === LANG_EN ? langEN : langAR;
	useEffect(() => {
		const body = {
			id: id || "",
			password: password || ""
		};
		callValidateInvitation(body).then(result => {
			if (result.data?.canAccess) {
				fetchInvitation(body);
			}
		});
	}, [id, password]);

	useEffect(() => {
		let body: { id: string; page?: number; perPage?: number; email?: string; password?: string; manage?: boolean } = {
			id: id || "",
			password: password || "",
			page,
			perPage: MEMORIES_PER_PAGE
		};
		if (isManagedRoute) {
			body["manage"] = true;
		}
		fetchMemories(body);
	}, [id, page, password]);

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		if (value > 0) {
			setLoadingCount(0);
			setPage(value);
			let body: { id: string; page?: number; perPage?: number; email?: string; password?: string; manage?: boolean } = {
				id: id || "",
				password: password || "",
				page: value,
				perPage: MEMORIES_PER_PAGE
			};
			if (isManagedRoute) {
				body["manage"] = true;
			}
			fetchMemories(body);
		}
	};

	const deleteImage = (id: string) => {
		setDeleteImageId(id);
	};
	const UpdateImage = (id: string, state: boolean) => {
		setUpdateImage({ id, state: !state });
	};

	const handleDeleteImage = () => {
		callDeleteMemory({ _id: id as string, memoryItemId: deleteImageId as string }).then(({ data, error }) => {
			if (data) {
				setDeleteImageId(null);
				toast.success("Image has been deleted.", { autoClose: 3000 });
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};
	const handleUpdateImage = () => {
		callUpdateMemory({
			_id: id as string,
			memoryItemId: updateImage?.id as string,
			is_private: updateImage?.state as boolean
		}).then(({ data, error }) => {
			if (data) {
				setUpdateImage(null);
				toast.success("Image has been Updated successfully.", { autoClose: 3000 });
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const handleDownloadMemories = async () => {
		try {
			const token = localStorage.getItem("token");
			const link = document.createElement("a");
			link.href = `${process.env.REACT_APP_BACKEND_API}/download/${id}/memories?token=${token}`;
			link.target = "_blank";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (err) {
			console.error("Download failed", err);
		}
	};

	const handleShareMemories = () => {
		selectedInvitation && setShareInvitationMemories(true);
	};

	const handleRSVP = () => {
		setShowCommentModal(true);
	};

	if (isValidatingInvitation || isFetchingInvitation) {
		return (
			<Box display={"flex"} justifyContent={"center"} alignItems={"center"} height={"40vh"}>
				<CircularProgress />
			</Box>
		);
	}

	if (validationError && !isValidatingInvitation && !isFetchingInvitation && !selectedInvitation) {
		const errorMessage =
			"data" in (validationError as IError)
				? (validationError as IError).data.message
				: (validationError as IError).error;
		const isLocked = (errorMessage || "").includes("password");
		if (isLocked) {
			return <LockedInvitation errorMessage={errorMessage || ""} />;
		}
		return <NoDataFallback message={errorMessage} />;
	}
	const isLockedPlan =
		(selectedInvitation?.selectedPricingPlan ?? INVITATION_PRICING_PLANS_ENUM.FREE) <
		INVITATION_PRICING_PLANS_ENUM.SIGNATURE;

	if (selectedInvitation && isManagedRoute && isLockedPlan) {
		return (
			<LockedMemoriesWrapper>
				<Box className="icon_ring">
					<PhotoLibrary />
				</Box>
				<Typography className="views_value">{(data?.totalItems || 0).toLocaleString()}</Typography>
				<Typography className="views_label">Total Memories</Typography>
				<Typography className="teaser_text">
					Upgrade to Signature to unlock guest photo & video uploads, sharing, and bulk downloads for your wedding
					album.
				</Typography>
				<Box className="locked_row">
					{[Share, DownloadOutlined, CameraAltSharp].map((Icon, i) => (
						<Box key={i} className="locked_stat">
							<Icon />
							<Lock className="lock_badge" />
						</Box>
					))}
				</Box>
				<UpgradeBtn
					variant={"contained"}
					startIcon={<WhiteCrown />}
					onClick={() => {
						trackEvent("upgrade_btn", {
							scope: "Invitations",
							description: "Upgrade Button"
						});
						trackMetaEvent("upgrade_btn", { invitation_id: selectedInvitation._id }, true);
						trackTikTokEvent("upgrade_btn", { invitation_id: selectedInvitation._id });
						dispatch(setOpenPricingModal(true));
					}}
				>
					Upgrade to Signature
				</UpgradeBtn>
			</LockedMemoriesWrapper>
		);
	}

	if (selectedInvitation && !isManagedRoute && isLockedPlan) {
		return <NoDataFallback message={lang.RSVP_NOT_ENABLED} />;
	}

	if (
		selectedInvitation &&
		!selectedInvitation?.wedding_album &&
		!isManagedRoute &&
		!isValidatingInvitation &&
		!isFetchingInvitation
	) {
		return <NoDataFallback message={"The wedding album isn’t available right now."} />;
	}

	return (
		<MemoriesWrapper>
			<Box minHeight={"70vh"}>
				{!isFetchingMemories && !isManagedRoute && (
					<Box py={2} display={"flex"} justifyContent={"flex-end"} style={{ maxWidth: "1400px", margin: "auto" }}>
						<Button variant={"contained"} onClick={handleRSVP}>
							{lang.SEND_MEMORIES}
						</Button>
					</Box>
				)}

				{!isFetchingMemories && !isFetchingInvitation && selectedInvitation && !selectedInvitation?.wedding_album && (
					<Alert severity="warning">
						Wedding album is disabled. Guests can’t upload memories right now. Go to Settings → Wedding Album to enable
						it.
					</Alert>
				)}
				{isFetchingMemories || isFetchingInvitation ? null : isManagedRoute ? (
					<Box display={"flex"} justifyContent={"flex-end"} alignItems={"center"} className={"download-btn-wrapper"}>
						<Button
							variant={"contained"}
							startIcon={<DownloadOutlined />}
							disabled={false}
							onClick={handleDownloadMemories}
						>
							<Typography>Download</Typography>
						</Button>
						<Button variant={"contained"} startIcon={<Share />} onClick={handleShareMemories}>
							<Typography>Share</Typography>
						</Button>
					</Box>
				) : (
					<Box className={"album-header"}>
						<Typography variant={isSmallerScreen ? "subtitle1" : "h6"} fontWeight={"bold"}>
							{`${selectedInvitation?.groom_name} & ${selectedInvitation?.bride_name}'s  ${lang.WEDDING_MEMORIES}`}
						</Typography>
					</Box>
				)}

				{(isFetchingMemories || loadingCount < (data?.items || []).length / 2) && (
					<Box mt={2} display="flex" style={{ flexFlow: "row wrap" }} justifyContent={"center"} gap={2}>
						{loadingArray.map((_, index) => (
							<Box key={index} width={isSmallerScreen ? "100%" : "200px"}>
								<Skeleton variant={"rectangular"} height={"240px"} />
							</Box>
						))}
					</Box>
				)}
				{(data?.items || []).length === 0 && !isFetchingMemories && !isFetchingInvitation && (
					<NoDataFallback message={"No Memories"} />
				)}
				<ImageList variant="masonry" cols={isSmallerScreen ? 2 : 4} gap={10}>
					{(data?.items || []).map(item => {
						return (
							<MediaItem
								key={item._id}
								item={item}
								loadingCount={loadingCount}
								setLoadingCount={setLoadingCount}
								totalItems={(data?.items || []).length}
								onDelete={deleteImage}
								isManagedRoute={isManagedRoute}
								onUpdate={UpdateImage}
								onImageClick={
									!isManagedRoute && !item.video_url
										? () => {
												setSlideShowIndex(imageOnlyItems.findIndex(i => i._id === item._id));
												setSlideShowOpen(true);
											}
										: undefined
								}
							/>
						);
					})}
				</ImageList>
			</Box>
			{(data?.totalItems || 0) > MEMORIES_PER_PAGE && (
				<Box my={4} display={"flex"} justifyContent={"flex-end"}>
					<Stack spacing={2}>
						<Pagination
							count={Math.ceil((data?.totalItems || 0) / MEMORIES_PER_PAGE)}
							onChange={handlePageChange}
							page={page}
							siblingCount={1}
							boundaryCount={1}
						/>
					</Stack>
				</Box>
			)}
			{!!deleteImageId && (
				<ConfirmModal
					open={!!deleteImageId}
					title={"Delete image"}
					message={"Are you sure you want to delete this image?"}
					onConfirm={handleDeleteImage}
					onClose={() => setDeleteImageId(null)}
					confirmText={"Yes, Delete"}
					cancelText={"No, Cancel"}
					loading={isDeletingMemory}
				/>
			)}
			{!!updateImage && (
				<ConfirmModal
					open={!!updateImage}
					title={"Update image"}
					message={"Are you sure you want to update this image?"}
					onConfirm={handleUpdateImage}
					onClose={() => setUpdateImage(null)}
					confirmText={"Yes, Update"}
					cancelText={"No, Cancel"}
					loading={isUpdatingMutation}
				/>
			)}
			{shareInvitationMemories && (
				<ShareWeddingInvitationMemoriesModal
					data={selectedInvitation as Invitations}
					open={true}
					onClose={() => setShareInvitationMemories(false)}
				/>
			)}
			{showCommentModal && (
				<RsvpModal
					open={showCommentModal}
					canEdit={false}
					onClose={() => setShowCommentModal(false)}
					data={selectedInvitation as Invitations}
					password={password || ""}
					id={id || ""}
				/>
			)}
			{!isManagedRoute && slideShowOpen && (
				<ImageSlideShow
					open={slideShowOpen}
					selectedInvitationGallery={imageOnlyItems}
					initialSlideShowImage={slideShowIndex}
					handleCloseModal={() => setSlideShowOpen(false)}
				/>
			)}
		</MemoriesWrapper>
	);
};

export default Memories;
